"use client";

import dynamic from "next/dynamic";
import { useEffect, useMemo, useRef, useState, type SyntheticEvent } from "react";
import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { AssetImage } from "@/components/optilase/asset-image";
import { ClinicLink } from "@/components/clinic/clinic-link";
import { sectionSpacing } from "@/components/optilase/section-spacing";
import { resolveBg } from "@/lib/bg-color";
import { byName, mergeRegionalPair, type Clinic } from "@/lib/clinics";
import { useRovingFocus } from "@/lib/use-roving-focus";
import { ANALYTICS_EVENTS, track } from "@/lib/analytics";
import { usePublicPathname } from "@/lib/public-pathname";

const ClinicMap = dynamic(() => import("./clinic-map"), {
  ssr: false,
  loading: () => <div className="bg-teal-01 size-full animate-pulse" />,
});

export type ClinicMapExplorerBlok = SbBlokData & {
  title?: string;
  body?: string;
  clinics?: string[];
  bg_color?: unknown;
  spacing?: string;
};

const CARTO_KEY = process.env.NEXT_PUBLIC_CARTO_API_KEY || "";

type Filter = "all" | "ireland" | "uk";
type LatLng = { lat: number; lng: number };

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All clinics" },
  { id: "ireland", label: "Ireland" },
  { id: "uk", label: "Northern Ireland" },
];

function distanceKm(a: LatLng, b: LatLng): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function onIslandOfIreland(p: LatLng): boolean {
  return p.lat >= 51.3 && p.lat <= 55.5 && p.lng >= -10.6 && p.lng <= -5.3;
}

function formatDistance(km: number): string {
  return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
}

const EIRCODE = /^[AC-FHKNPRTV-Y][0-9]{2}[0-9A-Z]{4}$/;
const UK_POSTCODE = /^[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][A-Z]{2}$/;

function normalizeSearch(raw: string): string {
  const collapsed = raw.trim().replace(/\s+/g, " ");
  const compact = collapsed.replace(/\s+/g, "").toUpperCase();
  if (EIRCODE.test(compact) || UK_POSTCODE.test(compact)) return compact;
  return collapsed;
}

function SearchIcon() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      aria-hidden
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" strokeLinecap="round" />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function LocateIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="3.5" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      className={className}
      aria-hidden
    >
      <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ResetIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 9V5a1 1 0 0 1 1-1h4M15 4h4a1 1 0 0 1 1 1v4M20 15v4a1 1 0 0 1-1 1h-4M9 20H5a1 1 0 0 1-1-1v-4" />
    </svg>
  );
}

function ClinicRow({
  clinic,
  num,
  active,
  distance,
  onSelect,
  onHover,
}: {
  clinic: Clinic;
  num: number;
  active: boolean;
  distance: number | null;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
}) {
  return (
    <button
      type="button"
      data-roving
      onClick={() => onSelect(clinic.uuid)}
      onMouseEnter={() => onHover(clinic.uuid)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(clinic.uuid)}
      onBlur={() => onHover(null)}
      aria-pressed={active}
      className={`group focus-visible:outline-teal-07 flex w-full cursor-pointer scroll-my-2 items-center gap-4 rounded-[12px] border p-4 text-left transition-colors outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 ${
        active
          ? "border-teal-02 bg-teal-02"
          : "border-grey-01 hover:border-teal-04 hover:bg-teal-01/60 bg-white"
      }`}
    >
      <span
        className={`flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
          active ? "bg-teal-07 text-white" : "bg-teal-02 text-teal-08"
        }`}
      >
        {num}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-grey-08 text-lg leading-[1.25] font-bold text-balance">
            {clinic.name}
          </span>
          {clinic.flagship && (
            <span className="bg-teal-02 text-teal-08 shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium tracking-wide uppercase">
              Primary
            </span>
          )}
        </span>
        {distance != null && (
          <span className="text-teal-07 mt-1 block text-sm font-bold">
            {formatDistance(distance)} away
          </span>
        )}
        {(clinic.addressLine1 || clinic.region) && (
          <span className="text-grey-06 mt-1 block text-sm leading-[1.56]">
            {[clinic.addressLine1, clinic.region].filter(Boolean).join(" · ")}
          </span>
        )}
      </span>
      <ChevronIcon className={active ? "text-teal-07" : "text-teal-04 group-hover:text-teal-07"} />
    </button>
  );
}

export default function ClinicMapExplorerView({
  blok,
  clinics,
}: {
  blok: ClinicMapExplorerBlok;
  clinics: Clinic[];
}) {
  const { bg, onDark, hex } = resolveBg(blok.bg_color, "white");
  const pathname = usePublicPathname();
  const isUk = pathname === "/uk" || pathname.startsWith("/uk/");
  const areaLabel = isUk ? "Postcode" : "Eircode";
  const areaHint = isUk ? "a postcode" : "an Eircode";
  const filterOrder: Filter[] = isUk ? ["all", "uk", "ireland"] : ["all", "ireland", "uk"];
  const orderedFilters = filterOrder.map((id) => FILTERS.find((f) => f.id === id)!);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const [filter, setFilter] = useState<Filter>("all");
  const [userLocation, setUserLocation] = useState<LatLng | null>(null);
  const [geoQuery, setGeoQuery] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState("");
  const [showBack, setShowBack] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listTopRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const { ref: filterGroupRef, rovingProps: filterRovingProps } = useRovingFocus<HTMLDivElement>();
  const { ref: listGroupRef, rovingProps: listRovingProps } = useRovingFocus<HTMLDivElement>();

  const distances = useMemo(() => {
    const m = new Map<string, number>();
    if (userLocation) {
      for (const c of clinics) {
        if (c.lat != null && c.lng != null) {
          m.set(c.uuid, distanceKm(userLocation, { lat: c.lat, lng: c.lng }));
        }
      }
    }
    return m;
  }, [clinics, userLocation]);

  const filtered = useMemo(() => {
    const list =
      filter === "ireland"
        ? byName(clinics).filter((c) => c.country === "Ireland")
        : filter === "uk"
          ? byName(clinics).filter((c) => c.country === "United Kingdom")
          : mergeRegionalPair(byName(clinics));
    if (!userLocation) return list;
    return [...list].sort(
      (a, b) => (distances.get(a.uuid) ?? Infinity) - (distances.get(b.uuid) ?? Infinity),
    );
  }, [clinics, filter, userLocation, distances]);

  const pins = useMemo(() => filtered.map((c, i) => ({ clinic: c, num: i + 1 })), [filtered]);

  const active = clinics.find((c) => c.uuid === activeId) ?? null;

  const applyLocation = (loc: LatLng, country?: string) => {
    setUserLocation(loc);
    setGeoLoading(false);
    setGeoError("");
    const located = clinics.filter(
      (c): c is Clinic & { lat: number; lng: number } => c.lat != null && c.lng != null,
    );
    const searchedIsland = onIslandOfIreland(loc);
    const sameLandmass = located.filter(
      (c) => onIslandOfIreland({ lat: c.lat, lng: c.lng }) === searchedIsland,
    );
    const inCountry = country ? located.filter((c) => c.country === country) : [];
    const pool = [...new Set([...inCountry, ...sameLandmass])];

    let nearest: (Clinic & { lat: number; lng: number }) | null = null;
    let best = Infinity;
    for (const c of pool.length ? pool : located) {
      const d = distanceKm(loc, { lat: c.lat, lng: c.lng });
      if (d < best) {
        best = d;
        nearest = c;
      }
    }

    const countryFilter: Filter = country === "United Kingdom" ? "uk" : "ireland";
    setFilter(country && nearest && nearest.country === country ? countryFilter : "all");
    setActiveId(nearest?.uuid ?? null);
  };

  const handleLocate = () => {
    if (!navigator.geolocation) {
      setGeoError("Location isn't available on this device.");
      return;
    }
    setGeoLoading(true);
    setGeoError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => applyLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => {
        setGeoLoading(false);
        clearLocationResult();
        setGeoError(
          err.code === err.PERMISSION_DENIED
            ? `Location access is blocked. Allow it in your browser settings, or enter ${areaHint}.`
            : `Couldn't get your location — try ${areaHint} instead.`,
        );
      },
      { timeout: 10000 },
    );
  };

  const clearLocationResult = () => {
    setUserLocation(null);
    setActiveId(null);
  };

  const handleGeocode = async (e: SyntheticEvent) => {
    e.preventDefault();
    const q = normalizeSearch(geoQuery);
    if (!q) return;

    const lower = q.toLowerCase();
    if (["uk", "gb", "united kingdom"].includes(lower)) {
      setFilter("uk");
      setGeoQuery("");
      setGeoError("");
      return;
    }
    if (["ie", "ireland", "eire", "republic of ireland"].includes(lower)) {
      setFilter("ireland");
      setGeoQuery("");
      setGeoError("");
      return;
    }

    setGeoLoading(true);
    setGeoError("");
    try {
      const res = await fetch(
        `/api/geocode/?q=${encodeURIComponent(q)}&region=${isUk ? "gb" : "ie"}`,
      );
      if (!res.ok) {
        setGeoLoading(false);
        clearLocationResult();
        setGeoError(
          res.status === 404
            ? `We couldn't find that — try ${areaHint} or town.`
            : "Lookup failed — please try again.",
        );
        return;
      }
      const data = (await res.json()) as LatLng & { country?: string };
      const clinicCountry =
        data.country === "GB" ? "United Kingdom" : data.country === "IE" ? "Ireland" : undefined;
      applyLocation({ lat: data.lat, lng: data.lng }, clinicCountry);
    } catch {
      setGeoLoading(false);
      clearLocationResult();
      setGeoError("Lookup failed — please try again.");
    }
  };

  const handleReset = () => {
    setActiveId(null);
    setHoveredId(null);
    setResetKey((k) => k + 1);
    setUserLocation(null);
    setGeoQuery("");
    setGeoError("");
    setFilter("all");
  };

  const selectFromList = (id: string) => {
    setActiveId(id);
    if (id !== activeId) {
      const picked = clinics.find((c) => c.uuid === id);
      track(ANALYTICS_EVENTS.CLINIC_SELECTED, {
        clinic: picked?.name,
        location: "clinic-finder",
      });
    }
    if (!window.matchMedia("(min-width: 1115px)").matches) {
      setShowBack(true);
      requestAnimationFrame(() =>
        mapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      );
    }
  };

  const backToList = () => {
    setShowBack(false);
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    const el = listTopRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setShowBack(false);
      },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const exitSection = () => {
    const section = sectionRef.current;
    if (!section) return;
    const all = Array.from(
      document.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
      ),
    );
    let last = -1;
    all.forEach((el, i) => {
      if (section.contains(el)) last = i;
    });
    all[last + 1]?.focus();
  };

  const mapItems = () => {
    const root = mapRef.current;
    if (!root) return [] as HTMLElement[];
    const view = root.querySelector<HTMLElement>(".leaflet-container")?.getBoundingClientRect();
    const inView = (el: HTMLElement) => {
      if (!view) return true;
      const r = el.getBoundingClientRect();
      const x = r.left + r.width / 2;
      const y = r.top + r.height / 2;
      return x >= view.left && x <= view.right && y >= view.top && y <= view.bottom;
    };
    const pins = Array.from(root.querySelectorAll<HTMLElement>(".leaflet-marker-icon")).filter(
      inView,
    );
    return [
      root.querySelector<HTMLElement>("[data-map-cta]"),
      ...pins,
      root.querySelector<HTMLElement>(".leaflet-control-zoom-in"),
      root.querySelector<HTMLElement>(".leaflet-control-zoom-out"),
      root.querySelector<HTMLElement>("[data-map-reset]"),
    ].filter((el): el is HTMLElement => Boolean(el));
  };

  const handleMapKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Tab" && !e.shiftKey) {
      if (!activeId) return;
      e.preventDefault();
      const list = listGroupRef.current;
      const row =
        list?.querySelector<HTMLElement>('[data-roving][tabindex="0"]') ??
        list?.querySelector<HTMLElement>("[data-roving]");
      row?.focus();
      return;
    }
    if (
      e.key === "ArrowDown" ||
      e.key === "ArrowRight" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowLeft"
    ) {
      const items = mapItems();
      if (items.length === 0) return;
      e.preventDefault();
      const i = items.indexOf(document.activeElement as HTMLElement);
      const forward = e.key === "ArrowDown" || e.key === "ArrowRight";
      const next = i < 0 ? 0 : (i + (forward ? 1 : items.length - 1)) % items.length;
      items[next]?.focus();
    }
  };

  useEffect(() => {
    const root = mapRef.current;
    if (!root) return;
    let raf = 0;
    let tries = 0;
    let observer: MutationObserver | null = null;

    const tagPins = () =>
      root
        .querySelectorAll<HTMLElement>(".leaflet-marker-icon")
        .forEach((el) => el.setAttribute("tabindex", "-1"));

    const setEntry = () => {
      const entry =
        root.querySelector<HTMLElement>("[data-map-cta]") ??
        root.querySelector<HTMLElement>(".leaflet-marker-icon") ??
        root.querySelector<HTMLElement>(".leaflet-control-zoom-in") ??
        root.querySelector<HTMLElement>("[data-map-reset]");
      entry?.setAttribute("tabindex", "0");
    };

    const apply = () => {
      const zoomIn = root.querySelector<HTMLElement>(".leaflet-control-zoom-in");
      if (!zoomIn && tries < 90) {
        tries += 1;
        raf = requestAnimationFrame(apply);
        return;
      }
      const neutralize = [
        zoomIn,
        root.querySelector<HTMLElement>(".leaflet-control-zoom-out"),
        root.querySelector<HTMLElement>("[data-map-reset]"),
        root.querySelector<HTMLElement>(".leaflet-container"),
        ...root.querySelectorAll<HTMLElement>("[data-map-osm] a"),
      ];
      for (const el of neutralize) el?.setAttribute("tabindex", "-1");
      tagPins();
      setEntry();

      if (!observer) {
        const pane = root.querySelector(".leaflet-marker-pane");
        if (pane) {
          observer = new MutationObserver(() => {
            tagPins();
            setEntry();
          });
          observer.observe(pane, { childList: true });
        }
      }
    };
    apply();
    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
    };
  }, [activeId, filtered.length]);

  if (clinics.length === 0) return null;

  return (
    <section
      {...storyblokEditable(blok)}
      ref={sectionRef}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          exitSection();
        }
      }}
      className={`${bg} px-5 md:px-10 lg:px-20 ${sectionSpacing(blok.spacing)}`}
    >
      <div className="mx-auto w-full max-w-7xl">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between lg:gap-3">
          <div className="flex flex-col gap-5 lg:max-w-201.5">
            {blok.title && (
              <h2 className={`text-section ${onDark ? "text-white" : "text-teal-07"}`}>
                {blok.title}
              </h2>
            )}
            {blok.body && (
              <p className={`text-xl leading-normal ${onDark ? "text-white/85" : "text-grey-06"}`}>
                {blok.body}
              </p>
            )}
          </div>

          <div className="flex w-full flex-col gap-2.5 lg:w-[396px]">
            <form onSubmit={handleGeocode} role="search" autoComplete="off" className="flex gap-2">
              <div className="relative flex-1">
                <span className="text-grey-06 pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2">
                  <SearchIcon />
                </span>
                <input
                  type="search"
                  name="clinic-search"
                  value={geoQuery}
                  onChange={(e) => setGeoQuery(e.target.value)}
                  placeholder={`${areaLabel} or town…`}
                  aria-label="Find your nearest clinic"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-1p-ignore
                  data-lpignore="true"
                  data-bwignore
                  data-form-type="other"
                  className={`border-grey-01 placeholder:text-grey-06 focus:border-teal-07 h-10 w-full rounded-full border bg-white pl-10 text-sm text-black transition-colors outline-none [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden ${geoQuery ? "pr-10" : "pr-4"}`}
                />
                {geoQuery && (
                  <button
                    type="button"
                    onClick={handleReset}
                    aria-label="Clear search"
                    className="text-grey-06 absolute top-1/2 right-2.5 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/8 transition-colors hover:bg-black/15 hover:text-black"
                  >
                    <ClearIcon />
                  </button>
                )}
              </div>
              <button
                type="submit"
                disabled={geoLoading}
                className="bg-navy-03 hover:bg-navy-02 inline-flex h-10 w-[100px] shrink-0 cursor-pointer items-center justify-center rounded-full font-medium text-white transition-colors disabled:opacity-60"
              >
                Find
              </button>
            </form>
            {geoError && <p className="text-brand-orange-02 text-xs font-medium">{geoError}</p>}
          </div>
        </div>

        <div
          ref={filterGroupRef}
          {...filterRovingProps}
          role="toolbar"
          aria-label="Filter clinics"
          className="border-grey-01 -mx-5 mt-5 flex scrollbar-none items-center gap-2 overflow-x-auto border-t px-5 pt-5 md:mx-0 md:flex-wrap md:overflow-visible md:px-0 lg:mt-10 lg:pt-10"
        >
          {orderedFilters.map((f) => (
            <button
              key={f.id}
              type="button"
              data-roving
              onClick={() => setFilter(f.id)}
              aria-pressed={filter === f.id}
              className={`focus-visible:outline-teal-07 inline-flex h-10 shrink-0 cursor-pointer items-center rounded-full px-6 font-medium transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-2 ${
                filter === f.id
                  ? "bg-teal-07 text-white"
                  : "border-grey-01 text-grey-06 hover:border-teal-04 hover:text-teal-07 border bg-white"
              }`}
            >
              {f.label}
            </button>
          ))}
          <button
            type="button"
            data-roving
            onClick={handleLocate}
            disabled={geoLoading}
            className="border-grey-01 text-grey-06 hover:border-teal-04 hover:text-teal-07 focus-visible:outline-teal-07 inline-flex h-10 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border bg-white px-6 font-medium transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-60"
          >
            <LocateIcon />
            {geoLoading ? "Locating…" : "Use my location"}
          </button>
        </div>

        <div className="mt-6 grid gap-6 lg:mt-10 lg:grid-cols-[minmax(0,456px)_minmax(0,1fr)]">
          <div ref={listRef} className="relative scroll-mt-24 lg:h-[556px]">
            <div
              ref={listTopRef}
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-1"
            />
            <div
              ref={listGroupRef}
              {...listRovingProps}
              role="group"
              aria-label="Clinics"
              className="flex [scrollbar-width:none] flex-col gap-2 lg:-mx-1.5 lg:h-full lg:overflow-y-auto lg:px-1.5 lg:pt-1.5 lg:pb-24 [&::-webkit-scrollbar]:hidden"
            >
              {filtered.length === 0 ? (
                <p className="border-grey-01 text-grey-06 rounded-[12px] border border-dashed p-10 text-center text-sm">
                  No clinics in this region.
                </p>
              ) : (
                filtered.map((c, i) => (
                  <ClinicRow
                    key={c.uuid}
                    clinic={c}
                    num={i + 1}
                    active={c.uuid === activeId}
                    distance={distances.get(c.uuid) ?? null}
                    onSelect={selectFromList}
                    onHover={setHoveredId}
                  />
                ))
              )}
            </div>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 hidden h-20 lg:block"
              style={{ background: `linear-gradient(to top, ${hex}, transparent)` }}
            />
          </div>

          <div
            ref={mapRef}
            role="group"
            aria-label="Map"
            onKeyDown={handleMapKeyDown}
            className="flex scroll-mt-24 flex-col lg:h-[556px]"
          >
            {showBack && (
              <button
                type="button"
                onClick={backToList}
                className="border-grey-01 text-grey-06 hover:border-teal-04 hover:text-teal-07 mb-3 inline-flex h-10 w-fit cursor-pointer items-center gap-1.5 rounded-full border bg-white px-5 text-sm font-medium transition-colors lg:hidden"
              >
                <ChevronIcon className="rotate-180" />
                Back to list
              </button>
            )}
            <div className="bg-teal-01 relative isolate h-[365px] overflow-hidden rounded-3xl lg:h-auto lg:flex-1">
              <ClinicMap
                pins={pins}
                activeId={activeId}
                hoveredId={hoveredId}
                resetKey={resetKey}
                onSelect={setActiveId}
              />

              <div className="absolute inset-x-3 bottom-3 z-1000 flex flex-col items-start gap-2 lg:inset-x-4 lg:bottom-4">
                <button
                  type="button"
                  data-map-reset
                  onClick={handleReset}
                  className="text-teal-08 hover:bg-teal-01 inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-white px-3.5 py-2 text-sm font-semibold shadow-[0_6px_20px_-6px_rgba(5,29,45,0.28)] transition-colors"
                >
                  <ResetIcon />
                  Reset
                </button>

                {active && (
                  <div className="w-full rounded-[20px] bg-white p-4 shadow-[0_16px_40px_-20px_rgba(5,29,45,0.45)] lg:px-5 lg:py-4">
                    <div className="flex items-center gap-4 lg:gap-5">
                      {active.photo ? (
                        <div className="relative size-[68px] shrink-0 overflow-hidden rounded-[12px] lg:size-[72px]">
                          <AssetImage
                            src={active.photo.filename}
                            focus={active.photo.focus}
                            alt={active.photo.alt}
                            fill
                            sizes="72px"
                            className="object-cover"
                          />
                        </div>
                      ) : (
                        <div className="bg-teal-01 size-[68px] shrink-0 rounded-[12px] lg:size-[72px]" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-grey-06 text-xs font-medium tracking-wide uppercase">
                          {distances.has(active.uuid)
                            ? `Nearest · ${formatDistance(distances.get(active.uuid) as number)} away`
                            : `Selected · ${active.country}`}
                        </p>
                        <p className="text-grey-08 mt-1 text-lg leading-[1.2] font-bold">
                          {active.name}
                        </p>
                        <p className="text-grey-06 mt-1 hidden text-sm leading-[1.56] lg:block">
                          {[active.addressLine1, active.region, active.phone]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                        {active.region && (
                          <p className="text-grey-06 mt-1 text-sm lg:hidden">{active.region}</p>
                        )}
                      </div>
                      <ClinicLink
                        clinic={active}
                        data-map-cta
                        className="bg-brand-orange-02 hover:bg-brand-orange-03 hidden h-10 shrink-0 items-center justify-center rounded-[8px] px-7 font-bold text-white transition-colors lg:inline-flex"
                      >
                        View clinic
                      </ClinicLink>
                    </div>
                    <ClinicLink
                      clinic={active}
                      data-map-cta
                      className="bg-brand-orange-02 hover:bg-brand-orange-03 mt-4 flex h-10 items-center justify-center rounded-[8px] font-bold text-white transition-colors lg:hidden"
                    >
                      View clinic
                    </ClinicLink>
                  </div>
                )}
              </div>
            </div>
            <p data-map-osm className="text-grey-06 mt-2 shrink-0 text-right text-[11px]">
              ©{" "}
              <a
                href="https://www.openstreetmap.org/copyright"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-teal-07 hover:underline"
              >
                OpenStreetMap
              </a>{" "}
              contributors
              {CARTO_KEY ? (
                <>
                  {" "}
                  ©{" "}
                  <a
                    href="https://carto.com/attributions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-teal-07 hover:underline"
                  >
                    CARTO
                  </a>
                </>
              ) : null}
            </p>
          </div>
        </div>
      </div>
      <style jsx global>{`
        .leaflet-control-zoom {
          border: none !important;
          border-radius: 10px !important;
          overflow: hidden;
          box-shadow: 0 6px 20px -8px rgba(5, 29, 45, 0.35);
        }
        .leaflet-control-zoom a {
          color: #00645e;
          border-bottom-color: #e6e6e6;
        }
        .leaflet-control-zoom a:hover {
          background: #ecf9f9;
          color: #00645e;
        }
        .leaflet-control-zoom a:focus-visible {
          outline: none;
          box-shadow: inset 0 0 0 2px #00857c;
        }
        .leaflet-marker-icon:focus-visible {
          outline: 2px solid #00857c;
          outline-offset: 3px;
          border-radius: 9999px;
        }
      `}</style>
    </section>
  );
}
